import type { QuestionFigure } from './questionFigures';
/**
 * Canonical question types in display order, with their human-friendly labels.
 * Single source of truth for both iteration (dropdowns) and label lookup (badges, filters).
 *
 * Iteration:    Object.keys(QUESTION_TYPE_LABELS) — preserves insertion order
 * Lookup:       QUESTION_TYPE_LABELS[type]
 * Safe lookup:  QUESTION_TYPE_LABELS[type as QuestionType] ?? type
 */
export const QUESTION_TYPE_LABELS = {
    MCQ: 'MCQ',
    MCQ_MULTI: 'MCQ - Multiple Correct',
    NUMERICAL: 'Numerical',
    SINGLELINE: 'Single Line',
    FREEFORM: 'Free form',
    // Structured (multi-part) — Question has `parts: QuestionPart[]` with a shared stem.
    // Each part is a mini-question of any atomic type. See docs/FeatureDesigns/question-structure.md.
    // Phase 1A: only FREEFORM parts allowed; Phase 1B widens to MCQ/MCQ_MULTI/NUMERICAL/SINGLELINE.
    STRUCTURED: 'Structured (multi-part)',
} as const;

export type QuestionType = keyof typeof QUESTION_TYPE_LABELS;
// Asserted as a non-empty tuple so z.enum() and similar APIs can consume it directly.
export const QUESTION_TYPES = Object.keys(QUESTION_TYPE_LABELS) as [QuestionType, ...QuestionType[]];

/** Atomic question types a STRUCTURED question's part can take. Narrower
 *  than `QuestionType` — parts cannot themselves be STRUCTURED. */
export type PartType = 'MCQ' | 'MCQ_MULTI' | 'NUMERICAL' | 'SINGLELINE' | 'FREEFORM';

/** One lettered sub-part of a STRUCTURED question (e.g. "(a)", "(b)").
 *  Shared wire shape — backend validates this via `QuestionPartSchema` in
 *  `Backend/functions/src/shared/types.ts`; frontend forms bind to it
 *  directly. Single source of truth for the structure.
 *  See docs/FeatureDesigns/question-structure.md. */
export interface QuestionPart {
    /** Stable id within the question, e.g. "a", "b", "c". Never reorder or
     *  rename — submissions, analyses, and computed scores key into parts
     *  by this id. */
    partId: string;
    /** Optional display label override. Defaults to `partId` rendered as "(a)". */
    label?: string;
    /** Atomic part type (Phase 1B): FREEFORM scores via the LLM's per-part
     *  assessment; MCQ / MCQ_MULTI / NUMERICAL score deterministically from
     *  the student's explicit partAnswers. */
    type: PartType;
    /** Optional per-part statement, in addition to the question's shared stem.
     *  Path X for Phase 1A: parts content lives inside `Question.questionText`
     *  (authors type "Part (a) ... Part (b) ..." inline). This field stays
     *  optional in the schema for Phase 1B+ when parts gain their own
     *  diagrams/options and need an associated statement. */
    statement?: string;
    diagramUrl?: string;
    diagramHash?: string;
    /** Figures the statement places by `[[fig:id]]` marker. */
    figures?: QuestionFigure[];
    /** Part-level MCQ options. Used in Phase 1B onward. Structural shape
     *  mirrors the backend MCQOption + shared-ui MCQOption. */
    options?: Array<{
        label: string;
        text: string;
        imageUrl?: string;
        imageHash?: string;
        isCorrect?: boolean;
    }>;
    correctAnswer?: string;
    errorMargin?: number;
    /** Marks for this part. Required for STRUCTURED authoring in Phase 1A. */
    marks?: number;
    negativeMarks?: number;
    // Phase 1C note: choice / K-of-N logic across parts is NOT stored per-part.
    // It lives on `Question.requirement` (a PartRequirement tree) — see
    // partRequirement.ts and docs/FeatureDesigns/question-structure.md §Phase 1C.
}

/** A question's total marks for DISPLAY. Single-part uses the question's own
 *  `marks`; STRUCTURED sums `parts[].marks` (the top-level value isn't stored).
 *  Mirrors the backend `questionWorth`; returns 0 when nothing is set.
 *  See docs/FeatureDesigns/assignments-tests/questionset-show-marks.md. */
export function questionTotalMarks(
    q: { questionType?: string | null; marks?: number; parts?: Array<{ marks?: number }> | null },
): number {
    if (q.questionType === 'STRUCTURED' && q.parts?.length) {
        return q.parts.reduce((sum, p) => sum + (p.marks ?? 0), 0);
    }
    return q.marks ?? 0;
}

/**
 * Does this question type ask the student to STATE their answer, rather than
 * tick one of ours?
 *
 * The gate for the declared-final-answer box (and for accepting the field on
 * the wire). MCQ has the option grid; STRUCTURED answers per part; FREEFORM has
 * no single value to state. That leaves NUMERICAL — and, when someone asks for
 * it, SINGLELINE, which needs only its matching stage in `singlelineScorer`
 * before it can be added here.
 *
 * See docs/FeatureDesigns/assignments-tests/final-answer-target.md.
 */
export function takesDeclaredAnswer(questionType?: string | null): boolean {
    return questionType === 'NUMERICAL';
}

/** Human marks label, e.g. "1 mark" / "3 marks". Returns null for 0/absent so
 *  callers can skip rendering entirely. */
export function formatMarks(total: number): string | null {
    if (!total) return null;
    return `${total} ${total === 1 ? 'mark' : 'marks'}`;
}

/**
 * Single source of truth for "what text represents this question" in lists,
 * pickers, previews, print, and the backend `questionTextPreview` field.
 *
 * For a STRUCTURED question the real sub-questions live on `parts[].statement`
 * and `questionText` is only the shared stem — so rendering the stem alone is
 * incomplete. This appends each part's statement after the stem
 * (e.g. "If 25-4x ≤ 16, find:  (a) the smallest value of x …  (b) …").
 * For non-STRUCTURED questions (or STRUCTURED with no parts) it returns
 * `questionText` unchanged.
 *
 * Callers truncate/trim for their own density — this returns the full text.
 */
export function buildQuestionDisplayText(question: {
    questionType?: string | null;
    questionText?: string | null;
    parts?: ReadonlyArray<Pick<QuestionPart, 'partId' | 'statement'>> | null;
}): string {
    const stem = (question.questionText ?? '').trim();
    if (question.questionType !== 'STRUCTURED' || !question.parts?.length) return stem;
    const partsText = question.parts
        .map(p => {
            const s = (p.statement ?? '').trim();
            return s ? `(${p.partId}) ${s}` : `(${p.partId})`;
        })
        .join('  ');
    return stem ? `${stem}  ${partsText}` : partsText;
}

/**
 * Single source of truth for "what text represents this question's correct
 * answer" — companion to `buildQuestionDisplayText`. Feeds `{{q.correctAnswer}}`.
 *
 * STRUCTURED questions author answers per part (`parts[].correctAnswer`) and
 * this composes them as "(a) …" lines. The per-part answers are what the scorer
 * marks against, so they are what the prompts must see: a top-level
 * `correctAnswer` left on a STRUCTURED question (written by PDF extraction or
 * import before those paths sanitized) is NOT allowed to shadow them. It is
 * read only when no part states an answer — the one case where it is the
 * sole answer the question carries.
 *
 * Returns '' when nothing is authored; callers decide their own fallback/hide
 * behaviour.
 */
export function buildQuestionCorrectAnswerText(question: {
    questionType?: string | null;
    correctAnswer?: string | null;
    parts?: ReadonlyArray<Pick<QuestionPart, 'partId' | 'correctAnswer'>> | null;
}): string {
    if (question.questionType === 'STRUCTURED' && question.parts?.length) {
        const composed = question.parts
            .map(p => {
                const ans = (p.correctAnswer ?? '').trim();
                return ans ? `(${p.partId}) ${ans}` : null;
            })
            .filter((line): line is string => line !== null)
            .join('\n');
        if (composed) return composed;
    }
    return question.correctAnswer ?? '';
}

/**
 * Every correct option of a choice question, in option order.
 *
 * An MCQ_MULTI question has several, and a reader that took the first
 * `isCorrect` option showed "A" as the whole answer to a question whose key is
 * A, B and C (prod vyF7tsLpyPpqXnE1). The `isCorrect` flags decide; when no
 * option carries one, `correctAnswer` is read as the option text or as a
 * comma-separated list of labels ("A,B,C").
 */
export function correctOptions<T extends { label?: string; text?: string; isCorrect?: boolean }>(
    options: readonly T[] | null | undefined,
    correctAnswer?: string | null,
): T[] {
    if (!options?.length) return [];
    const flagged = options.filter(o => o.isCorrect);
    if (flagged.length) return flagged;
    const key = correctAnswer?.trim();
    if (!key) return [];
    const byText = options.filter(o => o.text === key);
    if (byText.length) return byText;
    const labels = new Set(key.split(/[\s,]+/).map(l => l.toLowerCase()).filter(Boolean));
    return options.filter(o => !!o.label && labels.has(o.label.toLowerCase()));
}

/** A student's explicit answer to one objective part of a STRUCTURED
 *  question (Phase 1B). Freeform parts have no entry — their work lives on
 *  the shared canvas/typed text. Sent on the analyze request as
 *  `partAnswers[]` and snapshotted onto `ModelInput.studentSolution`.
 *  See docs/FeatureDesigns/question-structure.md §Phase 1B Design. */
export interface QuestionPartAnswer {
    partId: string;
    /** MCQ / MCQ_MULTI parts: selected option labels (uppercase). */
    selectedOptions?: string[];
    /** NUMERICAL parts: the student's value exactly as typed. */
    numericalValue?: string;
    /** SINGLELINE parts: the student's short text answer exactly as typed. */
    textValue?: string;
}

/** Per-part result for a STRUCTURED question's sub-part. Persisted on:
 *    - `Submission.partResults` (one row per attempt).
 *    - `Feedback.partResults` (on the ModelOutput / aggregated feedback).
 *    - `QSetSessionQuestionResult.partResults` (denormalized for the result screen).
 *  Supersedes the legacy `partScores: Record<partId, ComputedScores>` shape.
 *  See docs/FeatureDesigns/question-structure.md. */
export interface QuestionPartResult {
    partId: string;
    submissionId?: string;
    /** False for parts the student didn't attempt (and, in Phase 1C, for
     *  parts not selected by the question's `requirement` — the un-chosen
     *  branch of an OR / the surplus beyond K). */
    attempted: boolean;
    correctnessScore?: number;
    correctnessScoreComment?: string;
    speedScore?: number;
    speedScoreComment?: string;
    approachScore?: number;
    approachScoreComment?: string;
    finalScore?: number;
    /** finalScore (0–10) scaled to the part's `marks`. The question-level
     *  marksAwarded is the sum of all parts' marksAwarded. */
    marksAwarded?: number;
    /** The part's `marks` at scoring time — makes the result self-contained
     *  so UIs can render "2/3 marks" without re-loading the question. Absent
     *  on rows scored before Phase 1B slice 4. */
    maxMarks?: number;
    /** Phase 1C: false for a part excluded by the question's `requirement`
     *  (the un-chosen OR branch, or surplus beyond K in a K-of-N group).
     *  Absent ⇒ the part counted normally. The results UI uses this to label
     *  "Not selected" distinctly from "Not attempted". */
    selected?: boolean;
}

/** Roman numerals a sub-part label realistically uses, in order. */
const ROMAN_SEQUENCE = ['i', 'ii', 'iii', 'iv', 'v', 'vi', 'vii', 'viii'];

/**
 * Does this FLAT question look like a multi-part one that was imported before
 * anybody ticked "this paper has multi-part questions"?
 *
 * The backstop for the one failure mode the import flag has: a teacher who does
 * not find the checkbox gets a case-study question folded into a single blob,
 * with no per-part marks and no per-part scoring, and nothing says so. This is
 * deterministic — no LLM, no cost — and drives an advisory banner, never a
 * block: it is a hint to re-import, and a false positive must cost the teacher
 * only the reading of one sentence.
 *
 * Conservative on purpose, because the shape it looks for is also the shape of
 * an MCQ's option list:
 *   - an option-answered question is never flagged; "(a) 5 (b) 7" under an MCQ
 *     stem is four answers to choose between, not four questions. Gated on the
 *     TYPE and not just on `options` being populated: an MCQ whose options
 *     failed to parse out of its text is an extraction defect, and telling the
 *     teacher to re-import it as multi-part would be the wrong repair.
 *   - the labels must RUN IN ORDER from the start of the sequence — (a),(b) or
 *     (i),(ii) — so a stray "(b)" in prose, or a chemistry "(aq)", matches
 *     nothing.
 *   - each label must open a line or follow whitespace, so `f(a)` and
 *     coordinates like `(a, b)` are not labels.
 *   - two labels minimum. One "(a)" is a lettered aside, not a part list.
 */

export function looksMultiPart(q: {
    questionType?: string | null;
    questionText?: string | null;
    options?: unknown[] | null;
}): boolean {
    if (q.questionType === 'STRUCTURED') return false;
    if (q.questionType === 'MCQ' || q.questionType === 'MCQ_MULTI') return false;
    if (q.options?.length) return false;
    const text = q.questionText ?? '';
    if (!text) return false;

    const labelled = (label: string): boolean =>
        new RegExp(`(?:^|\\s)\\(${label}\\)\\s|(?:^|\\n)\\s*${label}[).]\\s`, 'im').test(text);

    // Alphabetic: (a) then (b), and so on for as long as they keep coming.
    let alpha = 0;
    for (let i = 0; i < 8; i++) {
        if (!labelled(String.fromCharCode(97 + i))) break;
        alpha++;
    }
    if (alpha >= 2) return true;

    let roman = 0;
    for (const numeral of ROMAN_SEQUENCE) {
        if (!labelled(numeral)) break;
        roman++;
    }
    return roman >= 2;
}

/**
 * The question types a student filters by. Each group folds several stored
 * `questionType`s: MCQ takes MCQ_MULTI, and Text is everything answered in
 * writing — one-liners, multi-part structured questions and long-form freeform.
 * One definition, so the Subject page and the worksheet offer the same three.
 */
export const QUESTION_TYPE_GROUPS = [
    { key: 'mcq', label: 'MCQ', types: ['MCQ', 'MCQ_MULTI'] },
    { key: 'numerical', label: 'Numerical', types: ['NUMERICAL'] },
    { key: 'text', label: 'Text', types: ['SINGLELINE', 'STRUCTURED', 'FREEFORM'] },
] as const;

export type QuestionTypeGroupKey = (typeof QUESTION_TYPE_GROUPS)[number]['key'];

/** The filter group a stored questionType (any case) belongs to, if any. */
export function questionTypeGroupOf(questionType?: string | null): QuestionTypeGroupKey | undefined {
    const up = (questionType ?? '').toUpperCase();
    return QUESTION_TYPE_GROUPS.find(g => (g.types as readonly string[]).includes(up))?.key;
}
